import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BadgeComponent = Badge as any;
const ButtonComponent = Button as any;
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarIcon, Loader2, Upload, Info, Zap, Building2, Workflow, FileText, Gift, CreditCard, MessageSquare, Users } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from '@/components/ui/use-toast';
import api from '@/lib/api';

interface WhatsAppConnection {
  id: number;
  client_id: number;
  connection_type: 'oficial' | 'extra_oficial';
  name: string;
  is_active: boolean;
  is_online?: boolean;
  connection_status?: 'connected' | 'disconnected' | 'error' | 'unknown';
  last_checked_at?: string;
  chatconnect_account_id?: string;
  whatsapp_number?: string;
  phone_id?: string;
  business_account_id?: string;
  permanent_token?: string;
  chatconnect_oficial_inbox_id?: string;
  daily_limit?: number;
  sent_today?: number;
  min_interval_seconds?: number;
  max_interval_seconds?: number;
  delay_seg?: number;
  window_start?: string;
  window_end?: string;
  window_start_formatted?: string | null;
  window_end_formatted?: string | null;
  created_at?: string;
  updated_at?: string;
}

interface Tag {
  id: number;
  tag_code: string;
  tag_name: string;
  strategy_type: string;
  client_id?: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

interface ImportBoletosModalProps {
  clientId: number;
  onSuccess?: () => void;
  /** Quando true, o botão trigger assume destaque visual (CTA principal) */
  prominent?: boolean;
  /** Quando true, usa estilo claro (ideal sobre fundo escuro/hero) */
  inHero?: boolean;
}

interface ClientSystem {
  system_management: string;
  legal_name?: string;
  trade_name?: string;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

const ImportBoletosModal: React.FC<ImportBoletosModalProps> = ({ clientId, onSuccess, prominent, inHero }) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'boletos' | 'aniversariantes' | 'pagamentos'>('boletos');

  // Boletos
  const [tipoEnvio, setTipoEnvio] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<Date | undefined>();
  const [dataFim, setDataFim] = useState<Date | undefined>();

  // Aniversariantes
  const [dataAniversario, setDataAniversario] = useState<Date | undefined>(() => new Date());
  const [tagAniversario, setTagAniversario] = useState<string>('');
  const [tagsAniversario, setTagsAniversario] = useState<{ value: string; label: string; type: 'linked' | 'available' }[]>([]);

  // Pagamentos
  const [dataInicioPag, setDataInicioPag] = useState<Date | undefined>();
  const [dataFimPag, setDataFimPag] = useState<Date | undefined>();
  const [tagPagamento, setTagPagamento] = useState<string>('');
  const [tagsPagamento, setTagsPagamento] = useState<{ value: string; label: string; type: 'linked' | 'available' }[]>([]);

  const [availableTags, setAvailableTags] = useState<{ value: string; label: string; type: 'system' | 'custom' | 'linked' | 'available' }[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingClient, setLoadingClient] = useState(false);
  const [clientSystem, setClientSystem] = useState<ClientSystem | null>(null);
  const [availableConnections, setAvailableConnections] = useState<WhatsAppConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<number | null>(null);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [hasMultipleConnectionsForStrategy, setHasMultipleConnectionsForStrategy] = useState(false);
  const [showConnectionSelector, setShowConnectionSelector] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [tagLoadError, setTagLoadError] = useState<string | null>(null);
  // V2 (ÁtomosChat): múltiplas conexões V2 ativas. Quando uma é escolhida, as tags
  // são filtradas pelas estratégias V2 vinculadas a essa conexão.
  const [atomosConfigs, setAtomosConfigs] = useState<{ id: number; account_name: string; channel_name?: string | null; channel_phone?: string | null; is_active?: boolean }[]>([]);
  const [selectedAtomosConfigId, setSelectedAtomosConfigId] = useState<number | null>(null);
  // V2 — equipes salvas para a conexão escolhida (opcional)
  const [savedTeams, setSavedTeams] = useState<{ team_name: string; team_id: string }[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  const loadAvailableConnections = async () => {
    try {
      setLoadingConnections(true);
      const [response, configsRes] = await Promise.all([
        api.get(`/whatsapp/client/${clientId}`).catch(() => null) as Promise<any>,
        api.get(`/atomos-chat/client/${clientId}/configs`).catch(() => null) as Promise<{ success?: boolean; configs?: any[] } | null>,
      ]);
      if (response?.success) {
        const allConnections = response.connections || response.data || [];
        const officialConnections = allConnections.filter((c: WhatsAppConnection) => c.connection_type === 'oficial');
        setAvailableConnections(officialConnections);
        const activeConnections = officialConnections.filter((c: WhatsAppConnection) => c.is_active);
        setHasMultipleConnectionsForStrategy(activeConnections.length > 1);
        if (activeConnections.length === 1 && !selectedAtomosConfigId) setSelectedConnectionId(activeConnections[0].id);
      } else {
        setAvailableConnections([]);
      }
      const v2Configs = Array.isArray(configsRes?.configs) ? configsRes!.configs! : [];
      const activeV2 = v2Configs.filter((c: any) => c.is_active !== false);
      setAtomosConfigs(activeV2 as any);
    } catch (error) {
      setAvailableConnections([]);
      setAtomosConfigs([]);
    } finally {
      setLoadingConnections(false);
    }
  };

  const loadTagsForStrategy = async (strategyType: string) => {
    try {
      setLoadingTags(true);
      setTagLoadError(null);
      let linkedTags: Tag[] = [];
      let allClientTags: Tag[] = [];
      try {
        // V2: passar atomos_chat_config_id para filtrar estratégias daquela conexão V2.
        const v2Suffix = selectedAtomosConfigId ? `&atomos_chat_config_id=${selectedAtomosConfigId}` : '';
        const linkedRes = await api.get(`/tags/client/${clientId}/linked?type=${strategyType}${v2Suffix}`) as ApiResponse<Tag[]>;
        linkedTags = linkedRes.success ? linkedRes.data || [] : [];
      } catch { linkedTags = []; }
      try {
        const allRes = await api.get(`/tags/client/${clientId}/type/${strategyType}`) as ApiResponse<Tag[]>;
        allClientTags = allRes.success ? allRes.data || [] : [];
      } catch { allClientTags = []; }

      // Em V2, mostrar apenas as vinculadas àquela conexão (esconde "Disponíveis" para não confundir).
      const v2Mode = selectedAtomosConfigId != null;
      const combined = v2Mode
        ? linkedTags.filter((t: Tag) => t?.tag_code).map((t: Tag) => ({ value: t.tag_code, label: `${t.tag_name} (V2)`, type: 'linked' as const }))
        : [
            ...linkedTags.filter((t: Tag) => t?.tag_code).map((t: Tag) => ({ value: t.tag_code, label: `${t.tag_name} (Vinculada)`, type: 'linked' as const })),
            ...allClientTags.filter((t: Tag) => t?.tag_code && !linkedTags.some(l => l.id === t.id)).map((t: Tag) => ({ value: t.tag_code, label: `${t.tag_name} (Disponível)`, type: 'available' as const })),
          ];
      return combined;
    } catch (error) {
      setTagLoadError('Erro ao carregar tags');
      return [];
    } finally {
      setLoadingTags(false);
    }
  };

  const loadAvailableTags = async (tipo: string) => {
    let strategyType = 'COBRANCA';
    if (tipo.includes('LEMBRETE') || tipo === 'CARENCIA') strategyType = 'LEMBRETE';
    else if (tipo.includes('REVISTORIA')) strategyType = 'REVISTORIA';
    else if (tipo.includes('REATIVACAO')) strategyType = 'REATIVACAO';
    else if (tipo.includes('MARKETING') || tipo === 'CUSTOM') strategyType = 'LEMBRETE';
    else if (tipo === 'PAGAMENTO') strategyType = 'PAGAMENTO';
    else if (tipo.includes('COBRAN')) strategyType = 'COBRANCA';
    const tags = await loadTagsForStrategy(strategyType);
    setAvailableTags(tags);
    if (tags.length === 1) setSelectedTag(tags[0].value);
    else setSelectedTag('');
  };

  useEffect(() => {
    if (open && clientId && !clientSystem) {
      loadClientData();
      loadAvailableConnections();
    }
  }, [open, clientId]);

  useEffect(() => {
    if (tipoEnvio) loadAvailableTags(tipoEnvio);
    else { setAvailableTags([]); setSelectedTag(''); }
  }, [tipoEnvio, selectedAtomosConfigId]);

  useEffect(() => {
    if (activeTab === 'aniversariantes' && clientSystem?.system_management?.toLowerCase() === 'hinova') {
      loadTagsForStrategy('ANIVERSARIO').then(tags => {
        setTagsAniversario(tags);
        if (tags.length === 1) setTagAniversario(tags[0].value);
      });
    }
  }, [activeTab, clientSystem?.system_management]);

  useEffect(() => {
    if (activeTab === 'pagamentos') {
      loadTagsForStrategy('PAGAMENTO').then(tags => {
        setTagsPagamento(tags);
        if (tags.length === 1) setTagPagamento(tags[0].value);
      });
    }
  }, [activeTab]);

  // Carrega equipes salvas para a conexão V2 selecionada (campo opcional na UI)
  useEffect(() => {
    if (!selectedAtomosConfigId) {
      setSavedTeams([]);
      setSelectedTeamId('');
      return;
    }
    let cancelled = false;
    api.get(`/atomos-chat/client/${clientId}/saved-teams?config_id=${selectedAtomosConfigId}`)
      .then((r: any) => {
        if (cancelled) return;
        const list = (r?.teams ?? []) as { team_name: string; team_id: string }[];
        setSavedTeams(Array.isArray(list) ? list : []);
      })
      .catch(() => { if (!cancelled) setSavedTeams([]); });
    setSelectedTeamId('');
    return () => { cancelled = true; };
  }, [selectedAtomosConfigId, clientId]);

  const loadClientData = async () => {
    setLoadingClient(true);
    try {
      const response = await api.get(`/clients/${clientId}`) as ApiResponse<ClientSystem>;
      setClientSystem(response.success ? response.data || null : null);
    } catch {
      setClientSystem(null);
    } finally {
      setLoadingClient(false);
    }
  };

  const getTiposEnvio = () => {
    const sys = clientSystem?.system_management?.toLowerCase();
    if (sys === 'south') return [{ value: 'LEMBRETE', label: 'Lembrete' }, { value: 'COBRANCA', label: 'Cobrança' }, { value: 'CARENCIA', label: 'Carência' }, { value: 'REVISTORIA', label: 'Revistoria' }];
    if (sys === 'hinova') return [{ value: 'LEMBRETE', label: 'Lembrete' }, { value: 'COBRANCA', label: 'Cobrança' }, { value: 'REVISTORIA', label: 'Revistoria' }, { value: 'REATIVACAO', label: 'Reativação' }, { value: 'MARKETING', label: 'Marketing' }, { value: 'PAGAMENTO', label: 'Pagamento' }, { value: 'CUSTOM', label: 'Personalizado' }];
    if (sys === 'epta') return [{ value: 'LEMBRETE', label: 'Lembrete' }, { value: 'COBRANCA', label: 'Cobrança' }, { value: 'REVISTORIA', label: 'Revistoria' }];
    return [{ value: 'LEMBRETE', label: 'Lembrete' }, { value: 'COBRANCA', label: 'Cobrança' }, { value: 'REVISTORIA', label: 'Revistoria' }, { value: 'REATIVACAO', label: 'Reativação' }, { value: 'MARKETING', label: 'Marketing' }, { value: 'PAGAMENTO', label: 'Pagamento' }, { value: 'CUSTOM', label: 'Personalizado' }];
  };

  const getSystemInfo = () => {
    const sys = clientSystem?.system_management?.toLowerCase();
    if (sys === 'south') return { name: 'South', description: 'Sistema South com API especializada para gestão de proteção veicular', color: 'border-green-500 bg-green-50', icon: Building2 };
    if (sys === 'south-v0') return { name: 'South V0', description: 'Sistema South V0 com estrutura de dados em objeto com chaves numéricas', color: 'border-purple-500 bg-purple-50', icon: Building2 };
    if (sys === 'hinova') return { name: 'Hinova SGA', description: 'Sistema Hinova SGA com múltiplos endpoints especializados', color: 'border-blue-500 bg-blue-50', icon: Workflow };
    if (sys === 'epta') return { name: 'Epta', description: 'Sistema Epta com API integrada de títulos e cobrança', color: 'border-orange-500 bg-orange-50', icon: Workflow };
    return { name: 'Sistema não configurado', description: 'Configure o sistema de gestão nas configurações do cliente', color: 'border-gray-500 bg-gray-50', icon: Info };
  };

  const validateConnection = () => {
    // V2 escolhida: nada mais a validar aqui (a config V2 é validada no backend).
    if (selectedAtomosConfigId) return true;
    if (availableConnections.length > 0 && !selectedConnectionId) {
      toast({ title: 'Erro', description: 'Selecione uma conexão WhatsApp', variant: 'destructive' });
      return false;
    }
    if (hasMultipleConnectionsForStrategy && !selectedConnectionId) {
      toast({ title: 'Erro', description: 'Selecione uma conexão responsável pelo envio', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleImportBoletos = async () => {
    if (!tipoEnvio || !selectedTag || !dataInicio || !dataFim) {
      toast({ title: 'Erro', description: 'Preencha todos os campos obrigatórios', variant: 'destructive' });
      return;
    }
    if (!validateConnection()) return;

    setLoading(true);
    try {
      const sys = clientSystem?.system_management?.toLowerCase();
      const url = (sys === 'south' || sys === 'south-v0') ? '/south/importar-boletos' : sys === 'epta' ? '/epta/importar-boletos' : '/hinova/importar-boletos';
      const response = await api.post(url, {
        client_id: clientId,
        tipo_envio: tipoEnvio,
        data_inicio: format(dataInicio, 'dd/MM/yyyy'),
        data_fim: format(dataFim, 'dd/MM/yyyy'),
        tag: selectedTag || null,
        connection_id: selectedAtomosConfigId ? null : selectedConnectionId,
        atomos_chat_config_id: selectedAtomosConfigId || null,
        atomos_department_id: selectedAtomosConfigId ? (selectedTeamId || null) : null,
        atomos_department_name: selectedAtomosConfigId && selectedTeamId
          ? (savedTeams.find(t => t.team_id === selectedTeamId)?.team_name ?? null)
          : null,
      });
      toast({ title: 'Sucesso', description: (response as any).message || 'Boletos importados com sucesso' });
      setOpen(false);
      onSuccess?.();
    } catch (error: unknown) {
      toast({ title: 'Erro', description: (error as any)?.response?.data?.message || 'Erro ao importar boletos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImportAniversariantes = async () => {
    if (!dataAniversario || !tagAniversario) {
      toast({ title: 'Erro', description: 'Selecione a data e a tag', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/hinova/importar-aniversariantes', {
        client_id: clientId,
        data: format(dataAniversario, 'dd/MM/yyyy'),
        tag: tagAniversario,
        connection_id: selectedAtomosConfigId ? null : selectedConnectionId,
        atomos_chat_config_id: selectedAtomosConfigId || null,
        atomos_department_id: selectedAtomosConfigId ? (selectedTeamId || null) : null,
        atomos_department_name: selectedAtomosConfigId && selectedTeamId
          ? (savedTeams.find(t => t.team_id === selectedTeamId)?.team_name ?? null)
          : null,
      }) as any;
      toast({ title: 'Sucesso', description: response.message || 'Aniversariantes importados com sucesso' });
      setOpen(false);
      onSuccess?.();
    } catch (error: unknown) {
      toast({ title: 'Erro', description: (error as any)?.response?.data?.error || 'Erro ao importar aniversariantes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImportPagamentos = async () => {
    if (!tagPagamento || !dataInicioPag || !dataFimPag) {
      toast({ title: 'Erro', description: 'Preencha todos os campos obrigatórios', variant: 'destructive' });
      return;
    }
    if (!validateConnection()) return;

    setLoading(true);
    try {
      const response = await api.post('/hinova/importar-boletos', {
        client_id: clientId,
        tipo_envio: 'PAGAMENTO',
        data_inicio: format(dataInicioPag, 'dd/MM/yyyy'),
        data_fim: format(dataFimPag, 'dd/MM/yyyy'),
        tag: tagPagamento || null,
        connection_id: selectedAtomosConfigId ? null : selectedConnectionId,
        atomos_chat_config_id: selectedAtomosConfigId || null,
        atomos_department_id: selectedAtomosConfigId ? (selectedTeamId || null) : null,
        atomos_department_name: selectedAtomosConfigId && selectedTeamId
          ? (savedTeams.find(t => t.team_id === selectedTeamId)?.team_name ?? null)
          : null,
      });
      toast({ title: 'Sucesso', description: (response as any).message || 'Pagamentos importados com sucesso' });
      setOpen(false);
      onSuccess?.();
    } catch (error: unknown) {
      toast({ title: 'Erro', description: (error as any)?.response?.data?.message || 'Erro ao importar pagamentos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const systemInfo = getSystemInfo();
  const isHinova = clientSystem?.system_management?.toLowerCase() === 'hinova';

  const TagSelect = ({ value, onValueChange, tags, loading: tagLoading, emptyMessage }: {
    value: string;
    onValueChange: (v: string) => void;
    tags: { value: string; label: string }[];
    loading: boolean;
    emptyMessage: string;
  }) => (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="w-full h-11">
        <SelectValue placeholder="Selecione uma tag" />
      </SelectTrigger>
      <SelectContent>
        {tagLoading ? (
          <div className="px-3 py-4 text-center text-sm text-gray-500">Carregando...</div>
        ) : tags.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-gray-500">{emptyMessage}</div>
        ) : (
          tags.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)
        )}
      </SelectContent>
    </Select>
  );

  const ConnectionSelect = () => (
    (availableConnections.length > 0 || atomosConfigs.length > 0) && (
      <div className="space-y-2">
        <Label className="text-sm font-medium flex items-center gap-2">
          <Workflow className="h-4 w-4 text-blue-600" />
          Conexão WhatsApp
        </Label>
        {loadingConnections ? (
          <div className="text-sm text-gray-500 p-3 border rounded-md">Carregando...</div>
        ) : (
          <Select
            value={selectedAtomosConfigId ? `v2-${selectedAtomosConfigId}` : (selectedConnectionId?.toString() || '')}
            onValueChange={(v) => {
              if (v.startsWith('v2-')) {
                setSelectedAtomosConfigId(parseInt(v.slice(3), 10));
                setSelectedConnectionId(null);
              } else {
                setSelectedConnectionId(parseInt(v));
                setSelectedAtomosConfigId(null);
              }
              setSelectedTag('');
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Selecione a conexão" />
            </SelectTrigger>
            <SelectContent>
              {availableConnections.map(c => (
                <SelectItem key={`v1-${c.id}`} value={c.id.toString()}>
                  <span className="font-medium">{c.name}</span>
                  <BadgeComponent variant="outline" className="ml-2 text-xs">{c.connection_type}</BadgeComponent>
                </SelectItem>
              ))}
              {atomosConfigs.map(cfg => (
                <SelectItem key={`v2-${cfg.id}`} value={`v2-${cfg.id}`}>
                  <span className="font-medium">{cfg.account_name}</span>
                  <BadgeComponent variant="outline" className="ml-2 text-xs bg-teal-50 text-teal-800 border-teal-200">V2 ÁtomosChat</BadgeComponent>
                  {cfg.channel_phone && <span className="ml-2 text-xs text-gray-500">{cfg.channel_phone}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    )
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <ButtonComponent
          variant={prominent ? 'default' : 'outline'}
          className={
            prominent
              ? inHero
                ? 'flex items-center gap-3 px-6 py-6 h-auto text-base font-semibold bg-white text-slate-900 hover:bg-white/95 border-0 shadow-xl shadow-black/20 hover:shadow-2xl transition-all duration-300'
                : 'flex items-center gap-3 px-6 py-6 h-auto text-base font-semibold bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-700 hover:from-indigo-700 hover:via-violet-700 hover:to-purple-800 text-white border-0 shadow-xl shadow-violet-500/25 hover:shadow-2xl hover:shadow-violet-500/30 transition-all duration-300'
              : 'flex items-center gap-2'
          }
        >
          <div className={prominent ? 'p-2 rounded-lg bg-white/20' : undefined}>
            <MessageSquare className={prominent ? 'h-6 w-6' : 'h-5 w-5'} />
          </div>
          <span>Central da Comunicação</span>
        </ButtonComponent>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-indigo-500 via-purple-600 to-violet-600 rounded-xl shadow-lg">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold">Central da Comunicação</DialogTitle>
              <DialogDescription>
                Importe dados do sistema de gestão para envio de mensagens
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {loadingClient ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <>
            {systemInfo && (
              <Card className={`border-2 ${systemInfo.color} shadow-sm`}>
                <CardContent className="py-3">
                  <div className="flex items-center gap-2">
                    <systemInfo.icon className="h-4 w-4" />
                    <BadgeComponent variant="outline" className={systemInfo.color}>{systemInfo.name}</BadgeComponent>
                    {clientSystem?.legal_name && <span className="text-sm text-gray-600">{clientSystem.trade_name || clientSystem.legal_name}</span>}
                  </div>
                </CardContent>
              </Card>
            )}

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="mt-4">
              <TabsList className="grid w-full grid-cols-3 h-12 bg-muted/50">
                <TabsTrigger value="boletos" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Boletos
                </TabsTrigger>
                <TabsTrigger value="aniversariantes" className="flex items-center gap-2" disabled={!isHinova}>
                  <Gift className="h-4 w-4" />
                  Aniversariantes
                </TabsTrigger>
                <TabsTrigger value="pagamentos" className="flex items-center gap-2" disabled={!isHinova}>
                  <CreditCard className="h-4 w-4" />
                  Pagamentos
                </TabsTrigger>
              </TabsList>

              <TabsContent value="boletos" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2"><Workflow className="h-4 w-4" />Tipo de Envio</Label>
                  <Select value={tipoEnvio} onValueChange={setTipoEnvio}>
                    <SelectTrigger className="h-11"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
                    <SelectContent>
                      {getTiposEnvio().map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <ConnectionSelect />
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4" />Tag da Estratégia</Label>
                  <TagSelect value={selectedTag} onValueChange={setSelectedTag} tags={availableTags} loading={loadingTags} emptyMessage="Configure tags na aba Tags" />
                </div>
                {selectedAtomosConfigId && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" />Atribuir a uma equipe <span className="text-xs text-gray-500 font-normal">(opcional)</span></Label>
                    {savedTeams.length === 0 ? (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        Nenhuma equipe cadastrada para esta conexão V2. Edite a conexão V2 em <strong>Regra de Negócios → API de Envio</strong> e clique em <strong>Carregar equipes</strong> no passo 6.
                      </div>
                    ) : (
                      <Select value={selectedTeamId || '__none__'} onValueChange={(v) => setSelectedTeamId(v === '__none__' ? '' : v)}>
                        <SelectTrigger className="h-11"><SelectValue placeholder="— Sem equipe atribuída —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sem equipe atribuída —</SelectItem>
                          {savedTeams.map(t => (
                            <SelectItem key={t.team_id} value={t.team_id}>{t.team_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2"><CalendarIcon className="h-4 w-4" />Período de Vencimento</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <ButtonComponent variant="outline" className="h-11 justify-start font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dataInicio ? format(dataInicio, 'dd/MM/yyyy') : 'Data início'}
                        </ButtonComponent>
                      </PopoverTrigger>
                      <PopoverContent className="p-0"><Calendar mode="single" selected={dataInicio} onSelect={setDataInicio} locale={ptBR} /></PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <ButtonComponent variant="outline" className="h-11 justify-start font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dataFim ? format(dataFim, 'dd/MM/yyyy') : 'Data fim'}
                        </ButtonComponent>
                      </PopoverTrigger>
                      <PopoverContent className="p-0"><Calendar mode="single" selected={dataFim} onSelect={setDataFim} locale={ptBR} /></PopoverContent>
                    </Popover>
                  </div>
                </div>
                <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200/60">
                  <CardContent className="pt-4">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                      <ul className="text-xs text-blue-800 space-y-1">
                        <li>Período máximo: 30 dias</li>
                        <li>Boletos filtrados conforme o tipo de envio</li>
                        {systemInfo?.name === 'Hinova SGA' && <li>Múltiplos endpoints e autenticação OAuth</li>}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="aniversariantes" className="space-y-4 mt-4">
                {!isHinova ? (
                  <p className="text-sm text-gray-500 py-4">Disponível apenas para clientes com integração Hinova SGA.</p>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2"><CalendarIcon className="h-4 w-4" />Data do aniversário</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <ButtonComponent variant="outline" className="h-11 w-full justify-start font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {dataAniversario ? format(dataAniversario, 'dd/MM/yyyy') : 'Selecione o dia'}
                          </ButtonComponent>
                        </PopoverTrigger>
                        <PopoverContent className="p-0"><Calendar mode="single" selected={dataAniversario} onSelect={setDataAniversario} locale={ptBR} /></PopoverContent>
                      </Popover>
                      <p className="text-xs text-gray-500">Filtra associados que fazem aniversário nesta data</p>
                    </div>
                    <ConnectionSelect />
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4" />Tag da Estratégia</Label>
                      <TagSelect value={tagAniversario} onValueChange={setTagAniversario} tags={tagsAniversario} loading={loadingTags} emptyMessage="Configure estratégia de Aniversário na aba Estratégias" />
                    </div>
                    {selectedAtomosConfigId && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" />Atribuir a uma equipe <span className="text-xs text-gray-500 font-normal">(opcional)</span></Label>
                        {savedTeams.length === 0 ? (
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                            Nenhuma equipe cadastrada para esta conexão V2. Edite a conexão V2 em <strong>Regra de Negócios → API de Envio</strong> e clique em <strong>Carregar equipes</strong> no passo 6.
                          </div>
                        ) : (
                          <Select value={selectedTeamId || '__none__'} onValueChange={(v) => setSelectedTeamId(v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-11"><SelectValue placeholder="— Sem equipe atribuída —" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Sem equipe atribuída —</SelectItem>
                              {savedTeams.map(t => (
                                <SelectItem key={t.team_id} value={t.team_id}>{t.team_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                    <Card className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200/60">
                      <CardContent className="pt-4">
                        <div className="flex gap-3">
                          <Info className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                          <ul className="text-xs text-rose-800 space-y-1">
                            <li>Busca aniversariantes no Hinova (endpoint associado/aniversariante)</li>
                            <li>Envio manual além da automação por Cron</li>
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>

              <TabsContent value="pagamentos" className="space-y-4 mt-4">
                {!isHinova ? (
                  <p className="text-sm text-gray-500 py-4">Disponível apenas para clientes com integração Hinova SGA.</p>
                ) : (
                  <>
                    <ConnectionSelect />
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4" />Tag da Estratégia</Label>
                      <TagSelect value={tagPagamento} onValueChange={setTagPagamento} tags={tagsPagamento} loading={loadingTags} emptyMessage="Configure estratégia de Pagamento na aba Estratégias" />
                    </div>
                    {selectedAtomosConfigId && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" />Atribuir a uma equipe <span className="text-xs text-gray-500 font-normal">(opcional)</span></Label>
                        {savedTeams.length === 0 ? (
                          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                            Nenhuma equipe cadastrada para esta conexão V2. Edite a conexão V2 em <strong>Regra de Negócios → API de Envio</strong> e clique em <strong>Carregar equipes</strong> no passo 6.
                          </div>
                        ) : (
                          <Select value={selectedTeamId || '__none__'} onValueChange={(v) => setSelectedTeamId(v === '__none__' ? '' : v)}>
                            <SelectTrigger className="h-11"><SelectValue placeholder="— Sem equipe atribuída —" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Sem equipe atribuída —</SelectItem>
                              {savedTeams.map(t => (
                                <SelectItem key={t.team_id} value={t.team_id}>{t.team_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium flex items-center gap-2"><CalendarIcon className="h-4 w-4" />Período</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <ButtonComponent variant="outline" className="h-11 justify-start font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dataInicioPag ? format(dataInicioPag, 'dd/MM/yyyy') : 'Data início'}
                            </ButtonComponent>
                          </PopoverTrigger>
                          <PopoverContent className="p-0"><Calendar mode="single" selected={dataInicioPag} onSelect={setDataInicioPag} locale={ptBR} /></PopoverContent>
                        </Popover>
                        <Popover>
                          <PopoverTrigger asChild>
                            <ButtonComponent variant="outline" className="h-11 justify-start font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {dataFimPag ? format(dataFimPag, 'dd/MM/yyyy') : 'Data fim'}
                            </ButtonComponent>
                          </PopoverTrigger>
                          <PopoverContent className="p-0"><Calendar mode="single" selected={dataFimPag} onSelect={setDataFimPag} locale={ptBR} /></PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200/60">
                      <CardContent className="pt-4">
                        <div className="flex gap-3">
                          <Info className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                          <ul className="text-xs text-emerald-800 space-y-1">
                            <li>Importa pagamentos via Hinova SGA</li>
                            <li>Período máximo: 30 dias</li>
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="pt-6 gap-2 border-t mt-4">
              <ButtonComponent variant="outline" onClick={() => setOpen(false)} disabled={loading}>
                Cancelar
              </ButtonComponent>
              {activeTab === 'boletos' && (
                <ButtonComponent
                  onClick={handleImportBoletos}
                  disabled={loading || !tipoEnvio || !selectedTag || !dataInicio || !dataFim || !clientSystem}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importando...</> : <><Upload className="mr-2 h-4 w-4" />Importar Boletos</>}
                </ButtonComponent>
              )}
              {activeTab === 'aniversariantes' && isHinova && (
                <ButtonComponent
                  onClick={handleImportAniversariantes}
                  disabled={loading || !dataAniversario || !tagAniversario}
                  className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700"
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importando...</> : <><Gift className="mr-2 h-4 w-4" />Importar Aniversariantes</>}
                </ButtonComponent>
              )}
              {activeTab === 'pagamentos' && isHinova && (
                <ButtonComponent
                  onClick={handleImportPagamentos}
                  disabled={loading || !tagPagamento || !dataInicioPag || !dataFimPag}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                >
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importando...</> : <><CreditCard className="mr-2 h-4 w-4" />Importar Pagamentos</>}
                </ButtonComponent>
              )}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportBoletosModal;
